import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  createComponentAs,
  findNodePath,
  isDefined,
  select,
  setNodes,
  useEditorRef,
  useElement,
  useResizableStore,
  resizeLengthClamp,
  useResizeHandleProps,
} from '@udecode/plate';
import { useReadOnly } from 'slate-react';

export const useResizable = ({
  align = 'center',
  readOnly,
  minWidth = 92,
  maxWidth = '100%',
  renderHandleLeft,
  renderHandleRight,
  ...props
}) => {
  const element = useElement();
  const editor = useEditorRef();
  const _readOnly = isDefined(readOnly) ? readOnly : useReadOnly();
  const wrapperRef = useRef(null);

  const nodeWidth = element?.width ?? '100%';

  const [isDragging, setIsDragging] = useState(false);

  const [width, setWidth] = useResizableStore().use.width();

  const setNodeWidth = useCallback(
    (w) => {
      const path = findNodePath(editor, element);
      // Add width ratio for translating width of media to print.
      const wrapperStaticWidth = wrapperRef.current.offsetWidth;
      const widthRatio = wrapperStaticWidth / w;
      if (!path) return;

      if (w === nodeWidth) {
        // Focus the node if not resized
        select(editor, path);
      } else {
        setNodes(editor, { width: w, widthRatio }, { at: path });
      }
    },
    [editor, element, nodeWidth],
  );

  useEffect(() => {
    setWidth(nodeWidth);
  }, [nodeWidth, setWidth]);

  const handleResize = useCallback(
    ({ initialSize, delta, finished, direction }) => {
      const wrapperStaticWidth = wrapperRef.current.offsetWidth;
      const deltaFactor =
        (align === 'center' ? 2 : 1) * (direction === 'left' ? -1 : 1);

      const newWidth = resizeLengthClamp(
        initialSize + delta * deltaFactor,
        wrapperStaticWidth,
        {
          min: minWidth,
          max: maxWidth,
        },
      );

      if (finished) {
        if (wrapperStaticWidth === newWidth) {
          setNodeWidth('100%');
        } else {
          setNodeWidth(newWidth);
        }
        setIsDragging(false);
      } else {
        setWidth(newWidth);
      }
    },
    [align, maxWidth, minWidth, setNodeWidth, setWidth],
  );

  // Remove style except for cursor
  const handlePropsLeft = {
    ...useResizeHandleProps({
      direction: 'left',
      onResize: handleResize,
      onMouseDown: () => {
        setIsDragging(true);
      },
    }),
    style: { cursor: 'ew-resize' },
  };

  const handlePropsRight = {
    ...useResizeHandleProps({
      direction: 'right',
      onResize: handleResize,
      onMouseDown: () => {
        setIsDragging(true);
      },
    }),
    style: { cursor: 'ew-resize' },
  };

  const handleLeft =
    !_readOnly && align !== 'left' && renderHandleLeft?.(handlePropsLeft);
  const handleRight =
    !_readOnly && align !== 'right' && renderHandleRight?.(handlePropsRight);

  return {
    wrapperProps: {
      ref: wrapperRef,
      style: {
        position: 'relative',
      },
    },
    resizableProps: {
      style: {
        width,
        minWidth,
        maxWidth,
        position: 'relative',
      },
    },
    handleLeft,
    handleRight,
    isDragging,
    restProps: props,
  };
};

export const Resizable = createComponentAs(
  ({ children, ...props }) => {
    const { align } = props;
    const {
      wrapperProps,
      resizableProps,
      handleLeft,
      handleRight,
      isDragging,
      restProps,
    } = useResizable(props);

    const { style: wrapperStyle, ...restWrapper } = wrapperProps;
    if (align) {
      wrapperStyle.textAlign = align;
    }

    return (
      <div
        style={wrapperStyle}
        {...restWrapper}
      >
        <div
          {...resizableProps}
          {...restProps}
        >
          {handleLeft}
          <div
            style={{
              pointerEvents: isDragging ? 'none' : undefined,
            }}
          >
            {children}
          </div>
          {handleRight}
        </div>
      </div>
    );
  },
);
